//! Change in '../../../../../app/utils/date/date-utils-yyyy-mm-dd' as well

import { MS_IN_MINUTE } from '../../constants/date'

/**
 * @param date
 * @returns string YYYY-MM-DD
 */
export function formatDateYyyyMmDd(date: Date): string {
  return date.toISOString().split('T')[0]
}

/**
 * Calculate the UTC offset (in minutes) of `timeZone` at the instant `date`.
 * The result is the target zone's offset only and is independent of the
 * environment's local timezone, so it returns the same value whether the
 * viewer's browser is in Eastern, Central, Mountain, or Pacific time.
 * @param {Date} date - The instant to calculate the offset for.
 * @param {string} timeZone - The IANA timezone string.
 * @returns {number} - The timezone offset in minutes (negative west of UTC).
 */
export function getTimezoneOffsetMinutes(date: Date, timeZone: string): number {
  // Format the date for the given timezone
  const fmt = new Intl.DateTimeFormat('en-US', {
    timeZone,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hour12: false,
  })

  // Extract the parts (month, day, year, etc.)
  const parts = fmt.formatToParts(date).reduce((acc, part) => {
    if (part.type !== 'literal') acc[part.type] = part.value
    return acc
  }, {} as Record<string, string>)

  // Build a local time string that Date can parse
  const formatted = `${parts.year}-${parts.month}-${parts.day}T${parts.hour}:${parts.minute}:${parts.second}`

  // `formatted` is the target zone's wall-clock reading of `date`. Reading it
  // back as UTC gives the wall clock on the UTC timeline; the gap between that
  // and the true instant (`date`) is the target zone's offset. We must compare
  // against `date` itself — NOT `new Date(formatted)`, which the JS engine
  // parses in the *environment's* local timezone and would make the result
  // drift with the viewer's browser location.
  const utcTime = new Date(formatted + 'Z').getTime()

  const diffMs = utcTime - date.getTime()
  const diffMinutes = diffMs / MS_IN_MINUTE

  return diffMinutes
}
